import { useState, useEffect, useRef } from "react";
import { Instagram, Youtube, Facebook, Linkedin, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useClickTracking } from "@/hooks/useClickTracking";
import { ContactModal } from "./ContactModal";
import { useTranslation } from "react-i18next";
import { useMultilingualContent } from "@/hooks/useMultilingualContent";
import { useAdmin } from "@/hooks/useAdmin";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

export const Footer = () => {
  const { i18n, t } = useTranslation();
  const { transformContent } = useMultilingualContent();
  const { isAdmin } = useAdmin();

  // Helper for language-aware URLs (normalize language code)
  const currentLang = i18n.language?.split('-')[0] || 'pt';
  const languagePrefix = currentLang === 'pt' ? '' : `/${currentLang}`;

  // Get localized URLs for legal pages
  const getTermsUrl = () => {
    if (currentLang === 'en') return '/en/terms-of-service';
    if (currentLang === 'es') return '/es/terminos-de-servicio';
    return '/termos-de-servico'; // Portuguese
  };

  const getPrivacyUrl = () => {
    if (currentLang === 'en') return '/en/privacy-policy';
    if (currentLang === 'es') return '/es/politica-de-privacidad';
    return '/politica-de-privacidade'; // Portuguese
  };

  const { trackClick } = useClickTracking();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const tooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTouchDeviceRef = useRef(false);

  const [socialLinks, setSocialLinks] = useState<Record<string, string>>({
    hero_name: "",
    hero_title: "",
    footer_brand_name: "",
    footer_brand_title: "",
    footer_admin_label: "",
    footer_copyright: "",
    hero_instagram_url: "",
    hero_youtube_url: "",
    hero_tiktok_url: "",
    hero_twitter_url: "",
    hero_facebook_url: "",
    hero_linkedin_url: "",
  });

  const [socialMediaOrder, setSocialMediaOrder] = useState<Array<{
    platform_name: string;
    order_position: number;
    is_visible: boolean;
    is_featured: boolean;
  }>>([]);

  useEffect(() => {
    return () => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
      }
    };
  }, []);

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isTouchDeviceRef.current = true;

    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
    setIsTooltipOpen(true);
    tooltipTimerRef.current = setTimeout(() => {
      setIsTooltipOpen(false);
    }, 5000);
  };

  const handleClick = (e: React.MouseEvent) => {
    if (isTouchDeviceRef.current) {
      e.preventDefault();
      e.stopPropagation();
      isTouchDeviceRef.current = false;
      return;
    }
    setIsTooltipOpen(!isTooltipOpen);
  };

  useEffect(() => {
    const fetchData = async () => {
      // Fetch social links
      const { data: contentData } = await supabase
        .from("site_content")
        .select("content_key, content_value, content_value_en, content_value_es")
        .in("content_key", [
          "hero_name",
          "hero_title",
          "footer_brand_name",
          "footer_brand_title",
          "footer_admin_label",
          "footer_copyright",
          "hero_instagram_url",
          "hero_youtube_url",
          "hero_tiktok_url",
          "hero_twitter_url",
          "hero_facebook_url",
          "hero_linkedin_url",
        ]);

      if (contentData) {
        const transformed = transformContent(contentData);
        setSocialLinks(transformed);
      }

      // Fetch social media order
      const { data: orderData } = await supabase
        .from("social_media_order")
        .select("platform_name, order_position, is_visible, is_featured")
        .order("order_position");

      if (orderData) {
        setSocialMediaOrder(orderData);
      }
    };

    fetchData();

    // Subscribe to real-time updates for site_content changes
    const contentChannel = supabase.channel(`footer-content-changes-${Math.random().toString(36).slice(2)}`, {
      config: {
        broadcast: { self: true }
      }
    });

    contentChannel
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'site_content',
          filter: 'content_key=in.(hero_name,hero_title,footer_brand_name,footer_brand_title,footer_admin_label,footer_copyright,hero_instagram_url,hero_youtube_url,hero_tiktok_url,hero_twitter_url,hero_facebook_url,hero_linkedin_url)'
        },
        (payload) => {
          fetchData();
        }
      )
      .subscribe();

    // Subscribe to real-time updates for social media order changes
    const orderChannel = supabase.channel(`footer-social-order-changes-${Math.random().toString(36).slice(2)}`, {
      config: {
        broadcast: { self: true }
      }
    });

    orderChannel
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'social_media_order'
        },
        (payload) => {
          fetchData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(contentChannel);
      supabase.removeChannel(orderChannel);
    };
  }, [i18n.language]);

  return <footer className="py-8 md:py-12 px-4 border-t border-border/50 bg-secondary/10">
      <div className="container max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          {/* Brand */}
          <div className="text-center md:text-left">
            <h3 className="font-bold text-2xl gradient-text mb-2">{socialLinks.footer_brand_name || socialLinks.hero_name}</h3>
            <p className="text-sm text-muted-foreground font-medium">
              {socialLinks.footer_brand_title || socialLinks.hero_title}
            </p>
          </div>

          {/* Social Links */}
          <div className="flex gap-4">
            {socialMediaOrder
              .filter(platform => platform.is_visible)
              .map(platform => {
                const platformKey = `hero_${platform.platform_name}_url` as keyof typeof socialLinks;
                const url = socialLinks[platformKey];
                
                if (!url) return null;

                const renderIcon = () => {
                  switch (platform.platform_name) {
                    case 'instagram':
                      return <Instagram className="w-5 h-5" />;
                    case 'youtube':
                      return <Youtube className="w-5 h-5" />;
                    case 'tiktok':
                      return <TikTokIcon />;
                    case 'twitter':
                      return <XIcon />;
                    case 'facebook':
                      return <Facebook className="w-5 h-5" />;
                    case 'linkedin':
                      return <Linkedin className="w-5 h-5" />;
                    default:
                      return null;
                  }
                };

                const platformLabel = platform.platform_name.charAt(0).toUpperCase() + platform.platform_name.slice(1);

                const buttonClassName = platform.is_featured
                  ? "w-12 h-12 rounded-full bg-secondary hover:gradient-instagram hover:text-white transition-all duration-300 flex items-center justify-center hover:scale-110 hover:shadow-glow"
                  : "w-12 h-12 rounded-full bg-secondary hover:bg-accent hover:text-accent-foreground transition-all duration-300 flex items-center justify-center hover:scale-110";

                return (
                  <a 
                    key={platform.platform_name}
                    href={url}
                    target="_blank" 
                    rel="noopener noreferrer" 
                    aria-label={`Visit ${platformLabel}`}
                    className={buttonClassName}
                    onClick={() => trackClick('social', `Footer ${platformLabel}`, url)}
                  >
                    {renderIcon()}
                  </a>
                );
              })}
            
            {/* Contact Icon */}
            <button
              onClick={() => {
                setIsContactModalOpen(true);
                trackClick('button', 'Footer Contact', 'contact-modal');
              }}
              aria-label="Contact"
              className="w-12 h-12 rounded-full bg-secondary hover:bg-accent hover:text-accent-foreground transition-all duration-300 flex items-center justify-center hover:scale-110"
            >
              <Mail className="w-5 h-5" />
            </button>
          </div>

          {/* Copyright & Legal Links */}
          <div className="text-sm text-muted-foreground font-medium text-center md:text-right">
            <TooltipProvider>
              <Tooltip open={isTooltipOpen} onOpenChange={setIsTooltipOpen}>
                <TooltipTrigger asChild>
                  <div
                    className="cursor-pointer"
                    onClick={handleClick}
                    onTouchEnd={handleTouchEnd}
                  >
                    © {new Date().getFullYear()} {(socialLinks.footer_brand_name || socialLinks.hero_name)}™. {socialLinks.footer_copyright}
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-[250px] sm:max-w-[300px] text-center">
                  <p className="text-xs leading-relaxed">{t('footer.trademarkText')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <div className="flex flex-col gap-1 mt-2">
              <div className="flex gap-3 justify-center md:justify-end items-center">
                <Link
                  to={getTermsUrl()}
                  className="text-xs hover:text-accent transition-colors"
                  onClick={() => trackClick('navigation', 'Footer Terms Link', getTermsUrl())}
                >
                  {t('footer.termsOfService')}
                </Link>
                <span className="text-xs">•</span>
                <Link
                  to={getPrivacyUrl()}
                  className="text-xs hover:text-accent transition-colors"
                  onClick={() => trackClick('navigation', 'Footer Privacy Link', getPrivacyUrl())}
                >
                  {t('footer.privacyPolicy')}
                </Link>
              </div>
              {isAdmin && (
                <a href="/admin" target="_blank" rel="noopener noreferrer" className="text-xs hover:text-accent transition-colors inline-block" onClick={() => trackClick('navigation', 'Footer Admin Link', '/admin')}>
                  {socialLinks.footer_admin_label || "Admin"}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
      <ContactModal open={isContactModalOpen} onOpenChange={setIsContactModalOpen} />
    </footer>;
};